import { Navigate } from 'react-router-dom';
import { Skeleton, Stack } from '@mantine/core';
import { useAuth } from './auth-context';
import { usePermissions } from '../hooks/usePermissions';
import type { Role } from '@synapse/shared-types';

// WHY: Minimal fallback hierarchy for the six default roles.
// Used ONLY when the /api/auth/permissions endpoint is unreachable.
// This prevents complete access lockout if the API is down.
const FALLBACK_HIERARCHY: Record<string, string[]> = {
  owner: ['coordinator', 'operations', 'executive', 'board', 'viewer'],
  coordinator: ['operations', 'viewer'],
  executive: ['viewer'],
  board: ['viewer'],
  operations: ['viewer'],
  viewer: [],
};

/**
 * Resolve effective roles using the fallback hierarchy via BFS traversal.
 * WHY: When the permissions API is unreachable, we still need to resolve
 * inheritance for the six default roles to avoid locking users out entirely.
 */
function resolveWithFallback(userRoles: Role[], requiredRoles: Role[]): boolean {
  const effective = new Set<string>(userRoles);
  const queue = [...userRoles];
  while (queue.length > 0) {
    const current = queue.shift()!;
    const parents = FALLBACK_HIERARCHY[current];
    if (parents) {
      for (const p of parents) {
        if (!effective.has(p)) {
          effective.add(p);
          queue.push(p as Role);
        }
      }
    }
  }
  return requiredRoles.some((r) => effective.has(r));
}

/**
 * Resolve effective roles using the fetched hierarchy via BFS traversal.
 * WHY: Uses the API-provided hierarchy data so that custom roles and
 * dynamically updated inheritance are respected without code changes.
 */
function resolveWithHierarchy(
  userRoles: Role[],
  requiredRoles: Role[],
  hierarchy: Record<string, { parents: string[]; description: string }>,
): boolean {
  const effectiveRoles = new Set<string>(userRoles);
  const queue = [...userRoles];
  const visited = new Set<string>(userRoles);

  // WHY: BFS traversal of parent chain to collect all inherited roles
  while (queue.length > 0) {
    const current = queue.shift()!;
    const entry = hierarchy[current];
    if (entry) {
      for (const parent of entry.parents) {
        if (!visited.has(parent)) {
          visited.add(parent);
          effectiveRoles.add(parent);
          queue.push(parent as Role);
        }
      }
    }
  }

  return requiredRoles.some((r) => effectiveRoles.has(r));
}

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** Roles that grant access to this route. If empty, any authenticated user can access. */
  requiredRoles?: Role[];
}

export function ProtectedRoute({ children, requiredRoles }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading: authLoading, user } = useAuth();
  const { data: permissions, isLoading: permLoading, isError } = usePermissions();

  // WHY: Show skeleton while auth or permissions are loading to prevent
  // premature navigation decisions (Requirement 3.3)
  if (authLoading || permLoading) {
    return (
      <Stack p="xl" gap="md">
        <Skeleton height={40} />
        <Skeleton height={200} />
        <Skeleton height={200} />
      </Stack>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (requiredRoles && requiredRoles.length > 0 && user) {
    // WHY: If permissions endpoint failed, use fallback hierarchy for default roles
    // to prevent complete access lockout (Requirement 3.4)
    if (isError || !permissions) {
      if (!resolveWithFallback(user.roles, requiredRoles)) {
        return <Navigate to="/" replace />;
      }
    } else {
      // WHY: Use fetched hierarchy for inheritance resolution so that custom
      // roles and dynamic hierarchy changes are respected (Requirement 3.5, 9.1, 9.2)
      if (!resolveWithHierarchy(user.roles, requiredRoles, permissions.hierarchy)) {
        return <Navigate to="/" replace />;
      }
    }
  }

  return <>{children}</>;
}
